import { useCallback, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';

export interface LiveResult<T> {
  /** The latest query result; undefined while loading or after an error. */
  readonly data: T | undefined;
  readonly loading: boolean;
  /** Set when the query threw; `retry` runs it again. */
  readonly error: unknown;
  readonly retry: () => void;
}

type Outcome<T> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: unknown };

/**
 * A live query: re-runs when the data it read changes. Errors are returned instead of thrown so
 * every card can show its own error state. `deps` must list every value `query` closes over.
 */
export function useLive<T>(query: () => Promise<T> | T, deps: readonly unknown[]): LiveResult<T> {
  const [attempt, setAttempt] = useState(0);
  const outcome = useLiveQuery<Outcome<T>>(async () => {
    try {
      return { ok: true, value: await query() };
    } catch (error) {
      return { ok: false, error };
    }
  }, [...deps, attempt]);
  const retry = useCallback(() => setAttempt((count) => count + 1), []);
  if (outcome === undefined) return { data: undefined, loading: true, error: null, retry };
  if (!outcome.ok) return { data: undefined, loading: false, error: outcome.error, retry };
  return { data: outcome.value, loading: false, error: null, retry };
}
